import shortid from "shortid";
import { Glue42 } from "../../../glue";
import { Executor, JumpListUpdateActionSettings, JumpListUpdateSettings, JumpListSettingsInternal } from "../types";

const T42JumpListAction = "T42.JumpList.Action";

export interface ActionCallback {
    callbackId: string;
    callback: () => Promise<void>;
}

export class JumpListManager {

    private _executor: Executor;
    private _agm: Glue42.AGM.API;
    private _logger: Glue42.Logger.API;
    private _groupActionCallbacks: Map<string, ActionCallback[]> = new Map<string, ActionCallback[]>();
    private _registered = false;

    public init(executor: Executor, agm: Glue42.AGM.API, logger: Glue42.Logger.API) {
        this._executor = executor;
        this._agm = agm;
        this._logger = logger;

        this.registerCallbackMethod();
    }

    public setEnabled(windowId: string, enabled: boolean): Promise<void> {
        const settings: JumpListUpdateSettings = {
            enabled
        };

        return this._executor.updateJumpList(windowId, settings);
    }

    public createCategory(windowId: string, title: string, actions: Glue42.Windows.JumpListActionSettings[]): Promise<void> {
        this.validateActions(title, actions);
        const settings: JumpListUpdateSettings = {
            category: {
                title,
                operation: "create",
                actions: this.toUpdateActions(windowId, "create", title, actions)
            }
        };

        return this._executor.updateJumpList(windowId, settings);
    }

    public removeCategory(windowId: string, title: string) {
        const settings: JumpListUpdateSettings = {
            category: {
                title,
                operation: "remove",
                actions: []
            }
        };

        this.manageActionCallback(windowId, settings.category.operation, title);

        return this._executor.updateJumpList(windowId, settings);
    }

    public createActions(windowId: string, categoryTitle: string, actions: Glue42.Windows.JumpListActionSettings[]): Promise<void> {
        this.validateActions(categoryTitle, actions);
        const settings: JumpListUpdateSettings = {
            category: {
                title: categoryTitle,
                operation: "update",
                actions: this.toUpdateActions(windowId, "create", categoryTitle, actions)
            }
        };

        return this._executor.updateJumpList(windowId, settings);
    }

    public removeActions(windowId: string, categoryTitle: string, actions: Glue42.Windows.JumpListActionSettings[]): Promise<void> {
        const settings: JumpListUpdateSettings = {
            category: {
                title: categoryTitle,
                operation: "update",
                actions: this.toUpdateActions(windowId, "remove", categoryTitle, actions)
            }
        };

        return this._executor.updateJumpList(windowId, settings);
    }

    public async getActions(windowId: string, catgoryTitle: string): Promise<Glue42.Windows.JumpListActionSettings[]> {
        const actions: Glue42.Windows.JumpListActionSettings[] = [];
        const configuration = await this.getJumpListSettings(windowId);
        const currentCategory = configuration.categories.find((category) => category.title === catgoryTitle);
        if (currentCategory) {
            currentCategory.actions.forEach((action) => {
                const actionCallback = this.getActionCallback(action.callbackId);
                if (actionCallback) {
                    action.callback = actionCallback.callback;
                }
                actions.push({
                    icon: action.icon,
                    callback: action.callback,
                    singleInstanceTitle: action.singleInstanceTitle,
                    multiInstanceTitle: action.multiInstanceTitle
                } as Glue42.Windows.JumpListActionSettings);
            });
        }
        return Promise.resolve(actions);
    }

    public getJumpListSettings(windowId: string): Promise<JumpListSettingsInternal> {
        return this._executor.getJumpList(windowId);
    }

    private toUpdateActions(windowId: string, operation: string, categoryTitle: string, actions: Glue42.Windows.JumpListActionSettings[]): JumpListUpdateActionSettings[] {
        return actions.map((action) => {
            const updateAction = {
                icon: action.icon,
                callback: action.callback,
                callbackId: shortid.generate(),
                singleInstanceTitle: action.singleInstanceTitle,
                multiInstanceTitle: action.multiInstanceTitle,
                operation
            } as JumpListUpdateActionSettings;

            this.manageActionCallback(windowId, operation, categoryTitle, updateAction);

            return updateAction;
        });
    }

    private manageActionCallback(windowId: string, operation: string, categoryTitle: string, updateAction?: JumpListUpdateActionSettings) {
        const groupCallbacksKey = `${categoryTitle}-${windowId}`;
        if (operation === "create") {
            if (!this._groupActionCallbacks.has(groupCallbacksKey)) {
                this._groupActionCallbacks.set(groupCallbacksKey, []);
            }

            const categoryActionCallbacks = this._groupActionCallbacks.get(groupCallbacksKey);
            categoryActionCallbacks.push({
                callbackId: updateAction.callbackId,
                callback: updateAction.callback
            });

        } else if (operation === "remove") {
            if (updateAction) {
                let categoryActionCallbacks = this._groupActionCallbacks.get(groupCallbacksKey) ?? [];
                categoryActionCallbacks = categoryActionCallbacks.filter((accCal) => accCal.callbackId !== updateAction.callbackId);
                if (categoryActionCallbacks.length === 0) {
                    this._groupActionCallbacks.delete(groupCallbacksKey);
                } else {
                    this._groupActionCallbacks.set(groupCallbacksKey, categoryActionCallbacks);
                }
            } else {
                this._groupActionCallbacks.delete(groupCallbacksKey);
            }
        }
    }

    private registerCallbackMethod() {
        if (this._registered) {
            return;
        }
        this._registered = true;

        try {
            this._agm.register(T42JumpListAction, (args: any, caller: any) => {
                const actionCallback = this.getActionCallback(args.callbackId);
                if (actionCallback) {
                    try {
                        actionCallback.callback();
                    } catch (e) {
                        this._logger.error("Unable to execute user callback for jump list action!", e);
                    }
                }
            });
        } catch (e) {
            this._logger.error(`Unable to register method ${T42JumpListAction} for invoking jump list action callbacks!`, e);
            return Promise.reject(e);
        }
    }

    private getActionCallback(callbackId: string): ActionCallback {
        let callbackAction: ActionCallback;
        [...this._groupActionCallbacks.values()].forEach((callbacks: ActionCallback[]) => {
            const callback = callbacks.find((cal) => cal.callbackId === callbackId);
            if (callback) {
                callbackAction = callback;
            }
        });
        return callbackAction;
    }

    private validateActions(category: string, actions: Glue42.Windows.JumpListActionSettings[]) {
        if (!(actions && actions.length > 0)) {
            throw new Error(`Category '${category}' doesn't contain any actions!`);
        }

        actions.forEach((action) => {
            if (!action.singleInstanceTitle) {
                throw new Error(`Category '${category}' contains an action with undefined singleInstanceTitle!`);
            }
            if (!action.multiInstanceTitle) {
                throw new Error(`Category '${category}' contains an action with undefined multiInstanceTitle!`);
            }
            if (!action.callback) {
                throw new Error(`Category '${category}' contains an action with undefined callback function!`);
            }
        });
    }
}

export default new JumpListManager();
